/*
 * Copyright (c) 2023 Bolmate B.V.
 */

import React, {useContext, useEffect, useRef, useState} from 'react';
import {ActivateHandle, PasswordErrors} from '../../../util/interfaces';
import {AppContext} from '../../misc/appContext';
import User from '../../../models/userModel';
import {ResponseData} from '@dvrd/dvr-controls';
import PasswordSettings from './passwordSettings';
import {onSetCrumbs} from '../../../util/events';
import {trackPlatformAction} from '../../../util/analytics';
import ValidateTwoFactorController from '../../twoFactor/validate/validateTwoFactorController';
import {disable2Fa} from '../../../util/requestSender';
import {createErrorMessage} from '../../../util/utils';
import EnableTwoFactorController from '../../twoFactor/enable/enableTwoFactorController';
import PageChangePermission from '../../misc/pageChangePermission';
import {PermissionCallback} from '../../../util/pageChanger';
import {showDialog, showSnackbar} from '@dvrd/dvr-controls';
import {useTranslation} from 'react-i18next';

const EMPTY_ERRORS: PasswordErrors = {confirmPassword: null, newPassword: null, password: null};

export default function PasswordSettingsController() {
    const context = useContext(AppContext);
    const {user} = context.userContext;
    const {t} = useTranslation();
    const [password, setPassword] = useState('');
    const [newPassword, setNewPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [loading, setLoading] = useState(false);
    const [errors, setErrors] = useState<PasswordErrors>(EMPTY_ERRORS);
    const [mfaEnabled, setMfaEnabled] = useState(user?.twoFactorEnabled ?? false);
    const mfaRef = useRef<ActivateHandle>(null);
    const enableMfaRef = useRef<ActivateHandle>(null);
    const hasChanged = useRef(false);

    function onChange(key: 'new' | 'confirm' | 'pass') {
        return function (value: string) {
            hasChanged.current = true;
            if (key === 'new') setNewPassword(value);
            else if (key === 'confirm') setConfirmPassword(value);
            else setPassword(value);
        };
    }

    function onResetError(key: keyof PasswordErrors) {
        return function () {
            setErrors(Object.assign({}, errors, {[key]: null}));
        };
    }

    function onDenyPageChange(id: number | null, callback: PermissionCallback) {
        if (hasChanged.current)
            onSubmit(() => {
                callback(id, true);
            })();
        else callback(id, true);
    }

    function onSubmit(callback?: VoidFunction) {
        return function () {
            if (!validateValues()) return;
            const {user} = context.userContext;
            if (!user) return;
            setLoading(true);
            user.changePassword(password, newPassword, (user: User, success: boolean, data: ResponseData) => {
                setLoading(false);
                if (success) {
                    trackPlatformAction('settings', 'change_password');
                    hasChanged.current = false;
                    showSnackbar(t('settings.password.changed', 'Je wachtwoord is gewijzigd'));
                    context.onChangeContext({user});
                    setPassword('');
                    setNewPassword('');
                    setConfirmPassword('');
                    callback?.();
                } else
                    showDialog(
                        createErrorMessage(
                            data.message ??
                                t('settings.password.changeFailedMsg', 'Het wijzigen van je wachtwoord is niet gelukt.')
                        ),
                        t('settings.password.changeFailedTitle', 'Wijzigen mislukt')
                    );
            });
        };
    }

    function onToggleTwoFactor(enabled: boolean) {
        if (enabled) enableMfaRef.current?.activate();
        else {
            mfaRef.current?.activate(null);
        }
    }

    function onTwoFactorSuccess(enabled: boolean) {
        return function (user: User) {
            if (enabled) return setMfaEnabled(user.twoFactorEnabled);
            disable2Fa({
                callback: (data: ResponseData) => {
                    if (data.success) {
                        showSnackbar(
                            t('settings.password.twoFactorDisabled', 'Tweestapsverificatie is uitgeschakeld'),
                            {
                                backgroundColor: 'color-green-whatsapp'
                            }
                        );
                        setMfaEnabled(user.twoFactorEnabled);
                    } else
                        showDialog(
                            createErrorMessage(
                                data.message ??
                                    t(
                                        'settings.password.twoFactorDisableFailedMsg',
                                        'Het uitschakelen van de tweestapsverificatie is niet gelukt.'
                                    )
                            ),
                            t('settings.password.disableFailedTitle', 'Uitschakelen mislukt')
                        );
                }
            });
        };
    }

    function validateValues() {
        let valid: boolean = true;
        const _errors = Object.assign({}, errors);
        if (!password) {
            valid = false;
            _errors.password = t('settings.password.errCurrent', 'Vul je huidige wachtwoord in');
        }
        if (!newPassword) {
            valid = false;
            _errors.newPassword = t('settings.password.errNew', 'Vul een nieuwe wachtwoord in');
        }
        if (!confirmPassword) {
            valid = false;
            _errors.confirmPassword = t('settings.password.errConfirm', 'Bevestig het nieuwe wachtwoord');
        } else if (confirmPassword !== newPassword) {
            valid = false;
            _errors.confirmPassword = t('settings.password.errMismatch', 'De wachtwoorden komen niet overeen');
        }
        if (!valid) setErrors(_errors);
        return valid;
    }

    useEffect(() => {
        onSetCrumbs([
            {label: t('settings.password.crumbSettings', 'Instellingen')},
            {label: t('settings.password.crumbSecurity', 'Beveiliging')}
        ]);
    }, []);

    return (
        <>
            <PasswordSettings
                onChange={onChange}
                onSubmit={onSubmit()}
                onResetError={onResetError}
                newPassword={newPassword}
                confirmPassword={confirmPassword}
                password={password}
                loading={loading}
                errors={errors}
                twoFactorEnabled={mfaEnabled}
                onToggleTwoFactor={onToggleTwoFactor}
            />
            <ValidateTwoFactorController
                onSuccess={onTwoFactorSuccess(false)}
                explanation={t(
                    'settings.password.twoFactorDisableExplanation',
                    'Voer de tweestapsverificatie code in om deze uit te schakelen.'
                )}
                ref={mfaRef}
            />
            <EnableTwoFactorController onSuccess={onTwoFactorSuccess(true)} ref={enableMfaRef} />
            <PageChangePermission
                changed={hasChanged.current}
                onDeny={onDenyPageChange}
                denyText={t('settings.password.saveChanges', 'Ja, wijzigingen opslaan')}
                allowText={t('settings.password.leavePage', 'Nee, pagina verlaten')}
                allowSecondary={true}
            >
                {t(
                    'settings.password.unsavedChanges',
                    'De wijzigingen gaan verloren als je de pagina verlaat. Wil je de wijzigingen eerst opslaan?'
                )}
            </PageChangePermission>
        </>
    );
}
